import { z } from "zod";
import { extractInputSchema } from "./action.js";
import { LIMITS, inputValuesSchema } from "./common.js";
import { HISTORY_OUTCOMES, attachmentMetaSchema } from "./message.js";
import { routeExampleSchema } from "./tenant.js";

/** One catalog entry as the router sees it. Only `extract` inputs are listed. */
export const catalogEntrySchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  inputs: z.array(extractInputSchema),
});
export type CatalogEntry = z.infer<typeof catalogEntrySchema>;

/** The history as the router sees it: previews, the action that ran, and its outcome. No ids. */
export const routedHistoryEntrySchema = z.strictObject({
  direction: z.enum(["in", "out"]),
  at: z.iso.datetime(),
  textPreview: z.string().max(LIMITS.textPreview),
  actionName: z.string().max(40).optional(),
  outcome: z.enum(HISTORY_OUTCOMES).optional(),
});
export type RoutedHistoryEntry = z.infer<typeof routedHistoryEntrySchema>;

/** What mailhand sends to a router adapter. */
export const routeRequestSchema = z
  .strictObject({
    tenant: z.strictObject({
      name: z.string().max(LIMITS.tenantName),
      instructions: z.string().max(LIMITS.instructions),
    }),
    catalog: z.array(catalogEntrySchema).min(1).max(50),
    message: z.strictObject({
      from: z.string(),
      fromName: z.string().optional(),
      subject: z.string().max(LIMITS.subject),
      text: z.string().max(LIMITS.messageText),
      attachments: z.array(attachmentMetaSchema).max(20),
      receivedAt: z.iso.datetime(),
    }),
    conversation: z.strictObject({
      history: z.array(routedHistoryEntrySchema).max(LIMITS.historyRouted),
    }),
    examples: z.array(routeExampleSchema).max(LIMITS.examples),
  })
  .meta({ description: "catalog[].inputs lists only inputs with source extract." });
export type RouteRequest = z.infer<typeof routeRequestSchema>;

const confidenceSchema = z.number().min(0).max(1);

/** What a router adapter returns. mailhand validates it and applies policy before anything runs. */
export const routeDecisionSchema = z.strictObject({
  actionId: z.string(),
  confidence: confidenceSchema,
  inputs: inputValuesSchema.refine((inputs) => Object.keys(inputs).length <= 10, "At most 10 inputs"),
  reason: z.string().min(1).max(LIMITS.reason).meta({ description: "One plain sentence for the business owner. No model talk." }),
  alternatives: z
    .array(z.strictObject({ actionId: z.string(), confidence: confidenceSchema }))
    .max(3)
    .optional(),
});
export type RouteDecision = z.infer<typeof routeDecisionSchema>;

export const POLICY_OUTCOMES = ["run", "awaiting_approval", "hold", "ignore"] as const;
export type PolicyOutcome = (typeof POLICY_OUTCOMES)[number];

export const ROUTER_ADAPTERS = ["haiku", "system-one"] as const;
export type RouterAdapterName = (typeof ROUTER_ADAPTERS)[number];
